/**
 * ModelCover — premium cover for any AI model.
 *
 * Strategy (in order of preference):
 *   1. REAL PHOTO  — `picsum.photos/seed/{model.id}/...` gives every model a
 *      unique, real, high-quality photograph that always loads (purpose-built CDN).
 *   2. PROCEDURAL  — if the photo ever fails (rare network glitch), a deterministic,
 *      hash-seeded SVG cover renders behind it so the card never goes blank.
 *   3. BRAND OVERLAY — a subtle category-tinted gradient + film grain + holographic
 *      sheen sits on top so the card feels intentional, not stock.
 *
 * Zero hard-coded URL lists → no 404s ever.
 */
import { useMemo, useState } from 'react';

/* ── deterministic 32-bit hash (xmur3) ─────────────────────────────────── */
function hash32(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

/* ── palette library — one per category/vibe ────────────────────────────── */
const PALETTES = {
  photorealistic: [['#FF6B4A', '#E040A0', '#1A0A1F']],
  anime:          [['#FF6B9D', '#A855F7', '#1E1B4B']],
  artistic:       [['#FBBF24', '#EC4899', '#1A0A1F']],
  typography:     [['#FFD93D', '#FB923C', '#0A0A0F']],
  design:         [['#34B76F', '#06B6D4', '#0A0A0F']],
  commercial:     [['#4A7CFF', '#A855F7', '#0A0A0F']],
  '3d':           [['#06B6D4', '#A855F7', '#0A0A0F']],
  i2v:            [['#34B76F', '#0EA5E9', '#0A0A0F']],
  animation:      [['#FBCFE8', '#A78BFA', '#0F0A1A']],
  cinematic:      [['#FCD34D', '#1A0A1F', '#000000']],
  general:        [['#FF6B4A', '#4A7CFF', '#0A0A0F']],
};

const TIER_GLOW = {
  premium:  '#FFD93D',
  standard: '#FF6B4A',
  fast:     '#34B76F',
};

function pickPalette(category, rand) {
  const group = PALETTES[category] || PALETTES.general;
  return group[Math.floor(rand() * group.length)];
}

/* ── geometric procedural fallback patterns ──────────────────────────────── */
function Orbs({ rand, color }) {
  const orbs = Array.from({ length: 6 }).map((_, i) => ({
    cx: 10 + rand() * 80, cy: 10 + rand() * 80,
    r: 20 + rand() * 35, op: 0.25 + rand() * 0.35, key: i,
  }));
  return (
    <g opacity="0.85" style={{ mixBlendMode: 'screen' }}>
      {orbs.map((o) => <circle key={o.key} cx={o.cx} cy={o.cy} r={o.r} fill={color} opacity={o.op} />)}
    </g>
  );
}
function Waves({ rand, color }) {
  const waves = Array.from({ length: 5 }).map((_, i) => {
    const y = 15 + i * 17; const amp = 4 + rand() * 6; const off = rand() * 100;
    let d = `M -10 ${y}`;
    for (let x = 0; x <= 120; x += 8) d += ` L ${x} ${y + Math.sin((x + off) * 0.15) * amp}`;
    return { d, key: i, op: 0.18 + rand() * 0.18 };
  });
  return (
    <g fill="none" stroke={color} strokeWidth="1.2" style={{ mixBlendMode: 'screen' }}>
      {waves.map((w) => <path key={w.key} d={w.d} opacity={w.op} />)}
    </g>
  );
}

/* ── main component ─────────────────────────────────────────────────────── */
export default function ModelCover({ model, size = 600, animate = true }) {
  const cover = useMemo(() => {
    const rand = hash32(model.id || model.name || 'x');
    const palette = pickPalette(model.category, rand);
    const [c1, c2, c3] = palette;
    const angle = Math.floor(rand() * 360);
    const PatternComp = rand() > 0.5 ? Orbs : Waves;
    const tierColor = TIER_GLOW[model.tier] || '#FF6B4A';
    const glyph = (model.name || '?').charAt(0).toUpperCase();
    // Picsum keyword nudge — adds a category-flavoured photo per seed
    const seed = `${model.id || model.name || 'x'}-${model.category || 'general'}`;
    const photoUrl = `https://picsum.photos/seed/${encodeURIComponent(seed)}/${size}/${Math.round(size * 1.2)}`;
    return { c1, c2, c3, angle, PatternComp, tierColor, glyph, rand, photoUrl };
  }, [model.id, model.name, model.category, model.kind, model.tier, size]);

  const { c1, c2, c3, angle, PatternComp, tierColor, glyph, rand, photoUrl } = cover;
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);

  return (
    <div className="absolute inset-0 d2-cover" data-testid={`model-cover-${model.id || ''}`}>
      {/* Layer 0 — procedural BACKDROP (always rendered, becomes the fallback if photo fails) */}
      <div
        className="absolute inset-0 d2-cover-bg"
        style={{ background: `linear-gradient(${angle}deg, ${c1} 0%, ${c2} 45%, ${c3} 100%)` }}
      />
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" style={{ opacity: 0.85 }}>
        <PatternComp rand={rand} color="#ffffff" />
      </svg>
      <div
        className="absolute d2-cover-blob"
        style={{
          width: `${50 + Math.floor(rand() * 30)}%`,
          height: `${50 + Math.floor(rand() * 30)}%`,
          background: `radial-gradient(circle, ${tierColor}66 0%, transparent 70%)`,
          top: `${Math.floor(rand() * 40)}%`,
          left: `${Math.floor(rand() * 40)}%`,
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span
          className="font-display font-black select-none"
          style={{ fontSize: `${size * 0.6}px`, color: '#ffffff', opacity: 0.06, lineHeight: 1, letterSpacing: '-0.05em', transform: 'translateY(-3%)' }}
        >
          {glyph}
        </span>
      </div>

      {/* Layer 1 — REAL PHOTO (primary). Hidden when not yet loaded or failed. */}
      {!photoFailed && (
        <img
          src={photoUrl}
          alt={model.name || 'AI model'}
          loading="lazy"
          decoding="async"
          onLoad={() => setPhotoLoaded(true)}
          onError={() => setPhotoFailed(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${photoLoaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}

      {/* Layer 2 — Category-tinted brand overlay (only when photo loaded) */}
      {photoLoaded && (
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-500"
          style={{
            background: `linear-gradient(${angle}deg, ${c1}55 0%, transparent 55%, ${c3}99 100%)`,
            mixBlendMode: 'multiply',
          }}
        />
      )}

      {/* Layer 3 — grain */}
      <div className="absolute inset-0 d2-cover-grain" />
      {/* Layer 4 — holographic sheen */}
      <div className={`absolute inset-0 d2-cover-sheen ${animate ? 'd2-cover-sheen--anim' : ''}`} />
    </div>
  );
}
